import type {
  BranchFcmTokens,
  CollaboratorPresenceReport,
  CollaboratorReportSummary,
} from '../types/collaborator-report.types';
import type {
  CollaboratorGender,
  CollaboratorJobDescription,
} from '../types/collaborator.types';
import { normalizeCollaborator } from './normalize-collaborators';

function parseNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return fallback;
}

function parseBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') {
      return true;
    }
    if (normalized === 'false' || normalized === '0') {
      return false;
    }
  }

  return fallback;
}

function readString(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return '';
}

export function normalizePresenceReport(
  raw: unknown,
): CollaboratorPresenceReport | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const item = raw as Record<string, unknown>;

  return {
    reportId: parseNumber(item.reportId),
    branchCode: readString(item.branchCode),
    employee: normalizeCollaborator(item.employee),
    date: readString(item.date),
    workedHours: parseNumber(item.workedHours),
    note: readString(item.note),
    rest: parseBoolean(item.rest),
    illness: parseBoolean(item.illness),
    holiday: parseBoolean(item.holiday),
    presentAtLunch: parseBoolean(item.presentAtLunch),
    presentAtDinner: parseBoolean(item.presentAtDinner),
  };
}

export function normalizePresenceReportsList(
  raw: unknown,
): CollaboratorPresenceReport[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item) => normalizePresenceReport(item))
    .filter((item): item is CollaboratorPresenceReport => item !== null);
}

export function normalizeReportSummary(
  raw: unknown,
): CollaboratorReportSummary | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const item = raw as Record<string, unknown>;
  const employeeId = parseNumber(item.employeeId);

  if (!employeeId) {
    return null;
  }

  return {
    employeeId,
    firstName: readString(item.firstName),
    lastName: readString(item.lastName),
    gender: readString(item.gender) as CollaboratorGender,
    jobDescription: readString(
      item.jobDescription,
    ) as CollaboratorJobDescription,
    dob: readString(item.dob),
    startDateInduction: readString(item.startDateInduction),
    endDateInduction: readString(item.endDateInduction),
    contractType: readString(item.contractType),
    phone: readString(item.phone),
    totalWorkedHours: parseNumber(item.totalWorkedHours),
    lunchCount: parseNumber(item.lunchCount),
    dinnerCount: parseNumber(item.dinnerCount),
    totalHolidays: parseNumber(item.totalHolidays),
    totalIllnessDays: parseNumber(item.totalIllnessDays),
    totalRestDays: parseNumber(item.totalRestDays),
    fired: parseBoolean(item.fired),
    visible: parseBoolean(item.visible, true),
  };
}

export function normalizeReportSummariesList(
  raw: unknown,
): CollaboratorReportSummary[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map((item) => normalizeReportSummary(item))
    .filter((item): item is CollaboratorReportSummary => item !== null);
}

export function normalizeBranchFcmTokens(raw: unknown): BranchFcmTokens {
  if (!raw || typeof raw !== 'object') {
    return { branchCode: '', branchName: '', fcmTokens: [] };
  }

  const item = raw as Record<string, unknown>;
  const tokens = Array.isArray(item.fcmTokens)
    ? item.fcmTokens.map((token) => readString(token)).filter(Boolean)
    : [];

  return {
    branchCode: readString(item.branchCode),
    branchName: readString(item.branchName),
    fcmTokens: tokens,
  };
}
